import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Star, MapPin, Clock, CheckCircle, Shield,
  Sparkles, Leaf, Waves, Wind, Wrench, ChevronRight, Filter
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  cat_diarista: { icon: Sparkles, name: "Diarista" },
  cat_jardinagem: { icon: Leaf, name: "Jardinagem" },
  cat_piscina: { icon: Waves, name: "Piscina" },
  cat_arcondicionado: { icon: Wind, name: "Ar-Condicionado" },
  cat_reforma: { icon: Wrench, name: "Reforma Leve" }
};

export default function ProvidersPage() {
  const { categoryId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  const config = CATEGORY_CONFIG[categoryId] || { icon: Sparkles, name: "Serviço" };
  const Icon = config.icon;

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await fetch(`${API_URL}/api/providers?category_id=${categoryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setProviders(data);
        }
      } catch (error) {
        console.error("Error fetching providers:", error);
        toast.error("Erro ao carregar prestadores");
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [categoryId, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="providers-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-slate-600" />
                <span className="font-semibold text-slate-900">{config.name}</span>
              </div>
            </div>
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Prestadores de {config.name}
          </h1>
          <p className="text-slate-600">
            {providers.length} profissionais disponíveis na sua região
          </p>
        </div>

        {providers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Icon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Nenhum prestador encontrado
              </h3>
              <p className="text-slate-500 mb-4">
                Ainda não temos prestadores cadastrados para esta categoria na sua região.
              </p>
              <Button onClick={() => navigate("/discovery")}>
                Explorar outras categorias
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {providers.map((provider) => (
              <Card key={provider.provider_id} className="card-hover overflow-hidden">
                <CardContent className="p-0">
                  {/* Provider Header */}
                  <div className="p-4 border-b border-slate-100">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {provider.user?.picture ? (
                          <img 
                            src={provider.user.picture} 
                            alt={provider.user?.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl font-bold text-slate-600">
                            {provider.user?.name?.charAt(0) || "P"}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {provider.user?.name || "Prestador"}
                          </h3>
                          {provider.is_verified && (
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          <span className="font-medium text-slate-900">
                            {provider.average_rating?.toFixed(1) || "0.0"}
                          </span>
                          <span className="text-slate-500 text-sm">
                            ({provider.total_reviews || 0} avaliações)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Provider Info */}
                  <div className="p-4 space-y-3">
                    {provider.description && (
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {provider.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {provider.experience_years > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          {provider.experience_years} anos exp.
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        <MapPin className="w-3 h-3 mr-1" />
                        {provider.service_radius_km}km raio
                      </Badge>
                      {provider.total_jobs > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {provider.total_jobs} serviços
                        </Badge>
                      )}
                    </div>

                    {provider.hourly_rate && (
                      <div className="pt-2 border-t border-slate-100">
                        <span className="text-sm text-slate-500">A partir de</span>
                        <p className="text-lg font-bold text-emerald-600">
                          R$ {provider.hourly_rate.toLocaleString("pt-BR")}/hora
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action */}
                  <div className="p-4 pt-0">
                    <Button 
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      onClick={() => navigate(`/providers/${categoryId}/${provider.provider_id}`)}
                      data-testid={`view-provider-${provider.provider_id}`}
                    >
                      Ver Perfil e Solicitar Orçamento
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Trust Banner */}
        <Card className="mt-8 bg-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Pagamento Seguro com Escrow</h3>
                <p className="text-slate-300 text-sm">
                  Seu dinheiro fica protegido até você aprovar o serviço realizado
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
